import { useState } from "react";
import { Send, Zap, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DocTypePicker } from "@/components/app/DocTypePicker";
import { api, formatApiError } from "@/lib/api";
import { LANGS, useApp } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

const URL_RX = /^https:\/\/(([\w-]+\.)?zoom\.us\/|meet\.google\.com\/[a-z0-9-]+|teams\.microsoft\.com\/|teams\.live\.com\/)/i;

export const BotForm = ({ configured, onSent }) => {
  const { L } = useApp();
  const { workspace, user } = useAuth();
  const [f, setF] = useState({ meeting_url: "", title: "", doc_type: "official", output_language: user?.output_language || "ms", consent: false });
  const [when, setWhen] = useState("now");
  const [joinAt, setJoinAt] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const validUrl = URL_RX.test(f.meeting_url.trim());

  const send = async () => {
    if (!validUrl) return toast.error(L("Gunakan pautan Zoom, Google Meet atau Microsoft Teams.", "Use a Zoom, Google Meet or Microsoft Teams link."));
    if (when === "later" && !joinAt) return toast.error(L("Pilih tarikh & masa.", "Pick a date & time."));
    if (!f.consent) return toast.error(L("Sila tandakan persetujuan rakaman.", "Please tick the recording consent."));
    setBusy(true);
    try {
      await api.post("/bots", { ...f, workspace_id: workspace.id, join_at: when === "later" ? new Date(joinAt).toISOString() : null });
      toast.success(when === "later" ? L("Bot dijadualkan.", "Bot scheduled.") : L("Bot sedang menyertai mesyuarat.", "Bot is joining the meeting."));
      setF((x) => ({ ...x, meeting_url: "", title: "", consent: false }));
      setJoinAt("");
      onSent?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const tab = (k, Icon, ms, en) => (
    <button type="button" onClick={() => setWhen(k)} data-testid={`bot-when-${k}`} aria-pressed={when === k}
      className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${when === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
      <Icon size={14} /> {L(ms, en)}
    </button>
  );

  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="bot-form">
      <div className="space-y-1.5 sm:col-span-2">
        <Label className="text-xs font-semibold text-slate-600">{L("Pautan mesyuarat", "Meeting link")}</Label>
        <Input value={f.meeting_url} onChange={(e) => set("meeting_url", e.target.value)} placeholder="https://meet.google.com/abc-defg-hij" className="h-11 rounded-xl bg-white" data-testid="bot-link-input" />
        {f.meeting_url && !validUrl && <p className="text-xs text-amber-700" data-testid="bot-link-invalid">{L("Pautan Zoom, Google Meet atau Teams diperlukan.", "A Zoom, Google Meet or Teams link is required.")}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
        <div className="flex rounded-xl border border-slate-200 bg-white p-1">
          {tab("now", Zap, "Sertai sekarang", "Join now")}
          {tab("later", CalendarClock, "Jadualkan", "Schedule")}
        </div>
        {when === "later" && <Input type="datetime-local" value={joinAt} onChange={(e) => setJoinAt(e.target.value)} className="h-10 w-auto rounded-xl bg-white" data-testid="bot-join-at-input" />}
      </div>
      <DocTypePicker value={f.doc_type} onChange={(v) => set("doc_type", v)} />
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-slate-600">{L("Tajuk (pilihan)", "Title (optional)")}</Label>
        <Input value={f.title} onChange={(e) => set("title", e.target.value)} maxLength={200} placeholder={L("Kosongkan untuk tajuk automatik", "Leave blank for automatic title")} className="h-10 rounded-xl bg-white" data-testid="bot-title-input" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-slate-600">{L("Bahasa hasil", "Output language")}</Label>
        <Select value={f.output_language} onValueChange={(v) => set("output_language", v)}>
          <SelectTrigger className="h-10 rounded-xl bg-white" data-testid="bot-output-language-select"><SelectValue /></SelectTrigger>
          <SelectContent>{LANGS.map(([k, ms, en]) => <SelectItem key={k} value={k}>{L(ms, en)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <label className="flex items-start gap-3 rounded-xl border border-violet-200 bg-violet-50/60 p-4 text-sm sm:col-span-2">
        <Checkbox checked={f.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" data-testid="bot-consent-checkbox" />
        <span>{L("Saya mengesahkan peserta dimaklumkan bahawa \"MinitAI Pencatat\" akan merakam mesyuarat ini, dan audio diproses oleh Recall.ai, OpenAI & ElevenLabs.", "I confirm participants are informed that \"MinitAI Pencatat\" will record this meeting, and audio is processed by Recall.ai, OpenAI & ElevenLabs.")}</span>
      </label>
      <div className="sm:col-span-2">
        <Button disabled={!configured || busy} onClick={send} className="h-11 rounded-xl bg-violet-600 px-6 hover:bg-violet-700" data-testid="send-bot-btn">
          <Send size={15} className="mr-2" /> {when === "later" ? L("Jadualkan bot", "Schedule bot") : L("Hantar bot sekarang", "Send bot now")}
        </Button>
      </div>
    </div>
  );
};
